/**
 * Extract a user-visible message from axios login / login-token errors.
 * Returns null when the UI should use i18n defaults (e.g. 401 without body message).
 */
export function extractApiErrorMessage(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (typeof d.message === 'string' && d.message.trim()) return d.message.trim();
  if (d.message && typeof d.message === 'object') {
    const inner = (d.message as Record<string, unknown>).message;
    if (typeof inner === 'string' && inner.trim()) return inner.trim();
  }
  const errs = d.errors;
  if (Array.isArray(errs) && errs[0] && typeof (errs[0] as { message?: string }).message === 'string') {
    return String((errs[0] as { message: string }).message).trim();
  }
  if (typeof d.error === 'string' && d.error.trim()) return d.error.trim();
  return null;
}

export function getLoginAxiosErrorMessage(error: any): string | null {
  const status = error?.response?.status as number | undefined;
  const data = error?.response?.data;
  const apiMsg = extractApiErrorMessage(data);
  if (apiMsg) return apiMsg;
  if (status === 401 || status === 403) return null;
  if (typeof error?.message === 'string' && error.message.trim()) return error.message.trim();
  return null;
}

export function getLoginTokenAxiosErrorMessage(error: any): string | null {
  const status = error?.response?.status as number | undefined;
  const data = error?.response?.data;
  const apiMsg = extractApiErrorMessage(data);
  if (apiMsg) return apiMsg;
  if (status === 401 || status === 403) return null;
  if (typeof error?.message === 'string' && error.message.trim()) return error.message.trim();
  return null;
}

/** Returns true if this 401 should not clear the whole auth session (e.g. failed password login). */
export function isLoginAuthRequest(config: { url?: string } | undefined): boolean {
  if (!config?.url) return false;
  let path = config.url.split('?')[0];
  if (path.includes('://')) {
    path = path.replace(/^https?:\/\/[^/]+/i, '');
  }
  path = path.replace(/^\//, '');
  return path === 'login' || path.endsWith('/login') || path === 'login-token' || path.endsWith('/login-token');
}
